import { StarIcon } from '@newcore/icons';
import { cn } from '@newcore/ui/cn';
import { Sizes } from '@newcore/ui/enums/sizes';
import { Tooltip } from '@newcore/ui/tooltips';
import { SvgIcon } from '@/components/ui/icons';
import { useToggleFavoriteMutation } from '@/hooks/use-toggle-favorite.queries';

type FavoriteButtonSize = Sizes.SM | Sizes.MD;

const BUTTON_SIZE_CLASSES: Record<FavoriteButtonSize, string> = {
  [Sizes.SM]: 'size-5.5',
  [Sizes.MD]: 'size-8',
};

const ICON_SIZE_CLASSES: Record<FavoriteButtonSize, string> = {
  [Sizes.SM]: 'size-3.75',
  [Sizes.MD]: 'size-4',
};

const HALO_STROKE_WIDTH = 5;

interface FavoriteButtonProps {
  applicationId: string;
  isFavorite: boolean;
  size?: FavoriteButtonSize;
}

export const FavoriteButton = ({
  applicationId,
  isFavorite,
  size = Sizes.MD,
}: FavoriteButtonProps) => {
  const toggleFavorite = useToggleFavoriteMutation();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    event.stopPropagation();
    toggleFavorite.mutate({ applicationId, isFavorite: !isFavorite });
  };

  const label = isFavorite ? 'Remove from favorites' : 'Add to favorites';
  const iconClassName = cn('relative', ICON_SIZE_CLASSES[size]);

  return (
    <Tooltip content={label}>
      <button
        type='button'
        aria-label={label}
        aria-pressed={isFavorite}
        className={cn(
          'relative flex items-center justify-center transition-colors',
          BUTTON_SIZE_CLASSES[size],
          isFavorite
            ? 'text-utility-yellow-500 hover:text-utility-yellow-700'
            : 'text-quaternary hover:text-utility-yellow-500'
        )}
        onClick={handleClick}
      >
        {size === Sizes.SM && (
          <SvgIcon
            name='starFilled'
            aria-hidden
            data-testid='favorite-button-halo'
            stroke='currentColor'
            strokeWidth={HALO_STROKE_WIDTH}
            strokeLinejoin='round'
            className={cn('absolute text-white', ICON_SIZE_CLASSES[size])}
          />
        )}
        {isFavorite ? (
          <SvgIcon name='starFilled' aria-hidden className={iconClassName} />
        ) : (
          <StarIcon className={iconClassName} />
        )}
      </button>
    </Tooltip>
  );
};
